import type { AxiosResponse } from 'axios';
import type { Board, Document, NodeField } from 'bc-artifacts';
import type {
  BriefsStats,
  ArtifactKey,
  BriefAccessRoleType,
  GroupReadType,
  NodeHydratedType,
  OrganizationReadType,
  TemplatableType,
  SnapshotData,
  BriefWithEffectiveRoleDbType,
  OTArtifactType,
  ContractPrincipal,
} from 'bc-model';
import { InvalidStateError } from 'bc-model';
import type { ArtifactType, OmitMeta } from '../stores/useArtifactStore/attachLenseToOT';
import yup from '../utils/yup';
import api, { succeedOn } from './api';
import type { AuthAccountWithPassType } from './accounts';
import type { TrashedCoreArtifact } from '../types';
import type { OTTransaction } from 'bc-ot';

type BriefAccessType = {
  accountId?: number;
  accountUuid?: string;
  groupId?: number;
  organizationId?: number;
  briefId: number;
  role: BriefAccessRoleType;
};

// principalId and principalUuid are optional during USE_ACCOUNT_UUID rollout:
// flag off: principalId required; flag on: principalUuid required for users
// Server enforces via validateBulkMembers; FE branches in useAddPlanningTeamMembers
// TODO[PIBE-246]: collapse to a single uuid identifier
type BulkAccessMember = {
  principalType: 'user' | 'group' | 'organization';
  principalId?: number;
  principalUuid?: string | null;
  role: BriefAccessRoleType;
};

type BulkAccessFailedResult = {
  principalType: 'user' | 'group' | 'organization';
  principalId?: number;
  principalUuid?: string | null;
  error: string;
};

type BulkAccessResult = {
  granted: Array<{
    principalType: 'user' | 'group' | 'organization';
    principalId?: number;
    principalUuid?: string | null;
    role: BriefAccessRoleType;
  }>;
  accessRequested: Array<{
    principalType: 'user' | 'group' | 'organization';
    principalId?: number;
    principalUuid?: string | null;
  }>;
  failed: Array<BulkAccessFailedResult>;
  briefAccess: BriefAccessSavedType[];
};

type BriefAccessSavedType = {
  id: number;
  account: AuthAccountWithPassType;
  group: GroupReadType;
  organization: OrganizationReadType;
} & BriefAccessType;

interface Favorite {
  [userId: number]: ArtifactKey[];
}

interface BriefType {
  accountId: number;
  key: string;
  title: string;
  favorites: Favorite;
  structure: ArtifactKey[];
}

type BriefNewType = {
  boards: {
    _new: Array<Board>;
  };
  nodeFields: {
    _new: Array<NodeField>;
  };
} & BriefType;

interface BriefSavedType extends ArtifactType, TemplatableType {
  accountId?: number | null;
  readmeId?: number | null;
  key: string;
  title: string;
  readonly effectiveRole: BriefAccessRoleType;
  readonly owner?: string;
  readonly ownerPrincipal?: unknown;
  favorites: Favorite;
  structure: ArtifactKey[];
  discoverable: boolean;
  classification: string;
  starterPlan?: boolean;
  isStarterPlanInstance?: boolean;
  description?: string | null;
  snapshotData?: SnapshotData | null;
  atoReferenceDateId?: number | null;
  atoCodeOffset?: number | null;
  organizationId?: number | null;
}

interface ReferencedBriefType {
  id: number;
  briefId: number;
  owner: string;
  key: string;
  title: string;
  isSnapshot: boolean;
}

type BriefWithOwner = {
  owner: string;
  ownerId: number;
  ownerType: 'account' | 'group' | 'organization';
  effectiveRole: BriefAccessRoleType;
} & BriefSavedType;

type BriefCollectionType = {
  [id: string]: BriefSavedType;
};

type BriefWithOwnerCollectionType = {
  [id: string]: BriefWithOwner;
};

type ListNodeTreeHydratedType = {
  node: NodeHydratedType;
  children: ListNodeTreeHydratedType[];
};

type ArtifactMap = {
  [key in OTArtifactType]: { [key: number]: ArtifactType };
} & {
  documents: { [key: number]: Document };
};
type BriefArtifactsResponse = {
  artifacts: ArtifactMap;
  transactions: OTTransaction[];
};

const briefValidationSchema = yup.object().shape({
  key: yup
    .string()
    .required('required')
    .min(3, 'too_short')
    .max(40, 'too_long')
    .matches(/^(?!(-|.*-{2,}))[a-zA-Z0-9-]*[a-zA-Z0-9]$/),
  title: yup.string().required('required').max(80, 'too_long'),
  classification: yup.string().max(140, 'too_long'),
});

function trimBriefPayload(brief: BriefSavedType): any {
  const {
    id,
    briefId,
    key,
    title,
    favorites,
    structure,
    template,
    globalTemplate,
    readmeId,
    discoverable,
    classification,
    description,
    snapshotData,
  } = brief;

  return {
    id,
    briefId,
    key,
    title,
    favorites,
    structure,
    template,
    globalTemplate,
    readmeId,
    discoverable,
    classification,
    description,
    snapshotData,
  };
}

function trimNewBriefPayload(brief: any): any {
  const {
    id,
    briefId,
    key,
    title,
    ownerPrincipal,
    boards,
    c2RelTypes,
    c2UnitStates,
    favorites,
    structure,
    template,
    globalTemplate,
    readmeId,
    discoverable,
    classification,
    description,
    snapshotData,
  } = brief;

  return {
    id,
    briefId,
    key,
    title,
    ownerPrincipal,
    boards,
    c2RelTypes,
    c2UnitStates,
    favorites,
    structure,
    template,
    globalTemplate,
    readmeId,
    discoverable,
    classification,
    description,
    snapshotData,
  };
}

async function createBrief(
  brief: OmitMeta<BriefSavedType>
): Promise<AxiosResponse<BriefSavedType>> {
  return api.post('/api/briefs/', trimNewBriefPayload(brief));
}

async function deleteBrief(briefId: number): Promise<AxiosResponse<BriefSavedType>> {
  return api.delete(`/api/brief/${briefId}`);
}

async function deleteBriefAccess(
  briefId: number,
  briefAccessId: number
): Promise<AxiosResponse<Nullable<BriefAccessSavedType>>> {
  return api.delete(`/api/brief/${briefId}/access/${briefAccessId}`);
}

async function deleteOwnBriefAccess(
  briefId: number,
  briefAccessId: number
): Promise<AxiosResponse<Nullable<BriefAccessSavedType>>> {
  return api.delete(`/api/brief/${briefId}/access/${briefAccessId}/leave`);
}

async function retrieveBrief(
  owner?: string,
  key?: string,
  briefId?: number
): Promise<AxiosResponse<BriefWithEffectiveRoleDbType>> {
  return briefId ? api.get(`/api/brief/${briefId}`) : api.get(`/api/brief/${owner}/${key}`);
}

async function retrieveBriefById(_: number): Promise<AxiosResponse<BriefWithEffectiveRoleDbType>> {
  const url = new URL(window.location.href);
  const [, owner, key, briefId] = url.pathname.split('/');
  if (!owner || !key) {
    throw new InvalidStateError('Unable to retrieve brief', { url });
  }

  return retrieveBrief(
    owner.replace('@', ''),
    key,
    briefId && briefId !== 'home' ? +briefId : undefined
  );
}

async function retrieveGlobalTemplateBriefs(): Promise<AxiosResponse<BriefWithOwner[]>> {
  return api.get('/api/briefs/global-templates');
}

async function retrieveBriefAccess(
  briefId: number,
  token?: string
): Promise<AxiosResponse<BriefAccessSavedType[]>> {
  const tokenString = token ? `?authToken=${token}` : '';
  // When a user lowers their own permissions, this will 403 trying to requery all user's access
  return api.get(`/api/brief/${briefId}/access${tokenString}`, succeedOn([403]));
}

async function createOrUpdateBriefAccess(
  _asUser: AuthAccountWithPassType,
  {
    briefId,
    role,
    principal,
  }: Pick<BriefAccessType, 'briefId' | 'role'> & { principal: ContractPrincipal }
): Promise<AxiosResponse<BriefAccessSavedType[]>> {
  const { principalType, principalId } = principal;

  await api.put<BriefAccessSavedType>(
    `/api/brief/${briefId}/access/${principalType}/${principalId}/${role}`
  );
  // Refreshes all user data naively by fetching it all
  return retrieveBriefAccess(briefId);
}

async function bulkCreateOrUpdateBriefAccess(
  _asUser: AuthAccountWithPassType,
  briefId: number,
  members: BulkAccessMember[],
  notifyByEmail: boolean
): Promise<AxiosResponse<BulkAccessResult>> {
  return api.post(`/api/brief/${briefId}/access/bulk`, { members, notifyByEmail });
}

async function retrieveBriefArtifacts(
  briefId: number
): Promise<AxiosResponse<BriefArtifactsResponse>> {
  return api.get(`/api/brief/${briefId}/artifacts/legacy`);
}

async function retrieveOwnBriefs(): Promise<AxiosResponse<BriefCollectionType>> {
  return api.get('/api/briefs/draft');
}

async function retrieveBriefs(): Promise<AxiosResponse<BriefWithOwnerCollectionType>> {
  return api.get('/api/briefs');
}

async function retrieveAccessibleBriefIds(): Promise<AxiosResponse<number[]>> {
  return api.get('/api/briefs/accessibleIds');
}

async function retrieveReferencedBriefs(
  briefId: number
): Promise<AxiosResponse<ReferencedBriefType[]>> {
  return api.get(`/api/brief/${briefId}/referenced`);
}

interface TrashedArtifactsResponse {
  trash: TrashedCoreArtifact[];
  count: number;
}

async function retrieveTrashedArtifacts(
  briefId: number,
  limit: number,
  offset: number,
  type?: string
): Promise<AxiosResponse<TrashedArtifactsResponse>> {
  return api.get(`/api/brief/${briefId}/trash?limit=${limit}&offset=${offset}&type=${type}`);
}

async function restoreTrashedArtifact(
  briefId: number,
  id: number,
  type: string,
  rank?: string
): Promise<AxiosResponse<ArtifactKey[]>> {
  return api.post(
    `/api/brief/${briefId}/trash/restore`,
    {
      type,
      id,
      rank,
    },
    succeedOn([404])
  );
}

async function updateBrief(
  brief: BriefSavedType,
  returning?: {
    edges?: boolean;
    nodes?: boolean;
    boards?: boolean;
    views?: boolean;
  }
): Promise<AxiosResponse<BriefSavedType>> {
  const { id: briefId } = brief;
  const { nodes: returnNodes, views: returnViews } = returning ?? {};
  return api.put(`/api/brief/${briefId}`, trimNewBriefPayload(brief), {
    params: {
      returnNodes,
      returnViews,
    },
  });
}

async function searchTeamUsers(
  briefId: number,
  query: string
): Promise<AxiosResponse<Array<AuthAccountWithPassType>>> {
  return api.get(`/api/brief/${briefId}/team/accounts`, { params: { query } });
}

async function searchTeamGroups(
  briefId: number,
  query: string
): Promise<AxiosResponse<Array<AuthAccountWithPassType>>> {
  return api.get(`/api/brief/${briefId}/team/groups`, { params: { query } });
}

async function searchTeamOrganizations(
  briefId: number,
  query: string
): Promise<AxiosResponse<Array<AuthAccountWithPassType>>> {
  return api.get(`/api/brief/${briefId}/team/organizations`, { params: { query } });
}

async function retrieveBriefsStats(ids: number[]): Promise<AxiosResponse<BriefsStats>> {
  return api.get(`/api/briefs/stats`, { params: { ids } });
}

async function retrieveStarterPlan(): Promise<AxiosResponse<BriefSavedType>> {
  return api.get('/api/briefs/starterPlan');
}

async function checkStarterPlanExists(): Promise<boolean> {
  const response = await api.get<boolean>('/api/briefs/starterPlan/exists');
  return response.data;
}

async function setStarterPlan(briefId: number, value: boolean): Promise<AxiosResponse<void>> {
  return api.put(`/api/brief/${briefId}/starterPlan`, { value });
}

type ArtifactFavoriteKey = {
  type: string;
  id: number;
};

type ArtifactFavoritesResponse = {
  briefId: number;
  artifactKeys: ArtifactFavoriteKey[];
};

async function retrieveArtifactFavorites(
  briefId?: number
): Promise<AxiosResponse<ArtifactFavoritesResponse[]>> {
  return api.get('/api/artifactFavorites', { params: briefId ? { briefId } : undefined });
}

async function updateArtifactFavorites(
  briefId: number,
  artifactKeys: ArtifactFavoriteKey[]
): Promise<AxiosResponse<void>> {
  return api.put('/api/artifactFavorites', { briefId, artifactKeys });
}

export {
  briefValidationSchema,
  checkStarterPlanExists,
  bulkCreateOrUpdateBriefAccess,
  createBrief,
  createOrUpdateBriefAccess,
  deleteBrief,
  deleteBriefAccess,
  deleteOwnBriefAccess,
  restoreTrashedArtifact,
  retrieveAccessibleBriefIds,
  retrieveBrief,
  retrieveBriefAccess,
  retrieveBriefArtifacts,
  retrieveBriefById,
  retrieveBriefs,
  retrieveBriefsStats,
  retrieveGlobalTemplateBriefs,
  retrieveOwnBriefs,
  retrieveReferencedBriefs,
  retrieveStarterPlan,
  retrieveTrashedArtifacts,
  updateArtifactFavorites,
  searchTeamGroups,
  searchTeamOrganizations,
  searchTeamUsers,
  setStarterPlan,
  retrieveArtifactFavorites,
  trimBriefPayload,
  trimNewBriefPayload,
  updateBrief,
};
export type {
  ArtifactFavoriteKey,
  BriefWithOwner,
  BriefAccessSavedType,
  BriefAccessType,
  ContractPrincipal,
  BulkAccessMember,
  BulkAccessResult,
  BulkAccessFailedResult,
  BriefCollectionType,
  BriefNewType,
  BriefSavedType,
  BriefType,
  BriefWithOwnerCollectionType,
  Favorite,
  ListNodeTreeHydratedType,
  ReferencedBriefType,
};
